const VIETNAM_TIMEZONE = 'Asia/Ho_Chi_Minh';

const pad2 = (value) => String(value).padStart(2, '0');

const formatFromParts = (year, month, day, hours, minutes, seconds) =>
  `${pad2(day)}/${pad2(month)}/${year} ${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;

export const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const raw = String(value).trim();
  if (!raw) return null;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatVietnamFromDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';

  try {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: VIETNAM_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const lookup = {};
    parts.forEach((part) => {
      if (part.type !== 'literal') {
        lookup[part.type] = part.value;
      }
    });

    return `${lookup.day}/${lookup.month}/${lookup.year} ${lookup.hour}:${lookup.minute}:${lookup.second}`;
  } catch {
    const vietnamTime = new Date(date.getTime() + 7 * 60 * 60 * 1000);
    return formatFromParts(
      vietnamTime.getUTCFullYear(),
      vietnamTime.getUTCMonth() + 1,
      vietnamTime.getUTCDate(),
      vietnamTime.getUTCHours(),
      vietnamTime.getUTCMinutes(),
      vietnamTime.getUTCSeconds()
    );
  }
};

export const normalizeVietnamDate = (value) => {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }

  const parsed = parseDateValue(value);
  if (!parsed) return '';
  const formatted = formatVietnamFromDate(parsed);
  return formatted ? formatted.slice(0, 10) : '';
};

export const normalizeVietnamTime = (value) => {
  const match = String(value || '').trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    return `${match[1]}:${match[2]}:${match[3] || '00'}`;
  }

  const parsed = parseDateValue(value);
  if (!parsed) return '';
  const formatted = formatVietnamFromDate(parsed);
  return formatted ? formatted.slice(11) : '';
};

export const formatVietnamDateTime = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return '';
  return formatVietnamFromDate(parsed);
};

export const formatVietnamDateTimeFromParts = (dateValue, timeValue, fallbackValue = null) => {
  const dateText = normalizeVietnamDate(dateValue);
  const timeText = normalizeVietnamTime(timeValue);

  if (dateText && timeText) {
    return `${dateText} ${timeText}`;
  }

  if (fallbackValue) {
    return formatVietnamDateTime(fallbackValue);
  }

  return '';
};

export const formatVietnamTimeFromParts = (dateValue, timeValue, fallbackValue = null) => {
  const timeText = normalizeVietnamTime(timeValue);
  if (timeText) return timeText;

  if (fallbackValue) {
    const full = formatVietnamDateTime(fallbackValue);
    return full ? full.slice(11) : '';
  }

  return '';
};

export const formatLocalMonthKey = (date = new Date()) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;

export const normalizeAttendanceDateKey = (record) => {
  const rawDate = record?.date;
  if (rawDate) {
    const match = String(rawDate).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const parsed = parseDateValue(record?.timestamp);
  if (!parsed) return null;
  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`;
};
